import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../../services/api.service';
import './BlogForm.css';

const BlogForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    blog_title: '',
    blog_by: '',
    blog: '',
    display_order: '0',
    display_status: '1',
    image: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isEditMode) {
      const fetchBlog = async () => {
        try {
          setFetchLoading(true);
          setError(null);
          const response = await apiService.getAdminBlogById(id);
          if (response.data.success) {
            const blog = response.data.data;
            setFormData({
              blog_title: blog.blog_title || '',
              blog_by: blog.blog_by || '',
              blog: blog.blog || '',
              display_order: blog.display_order || '0',
              display_status: blog.display_status || '1',
              image: blog.image || ''
            });
          } else {
            setError('Failed to load blog');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to load blog');
          console.error('Blog fetch error:', err);
        } finally {
          setFetchLoading(false);
        }
      };
      fetchBlog();
    }
  }, [id, isEditMode]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.blog_title.trim()) {
      newErrors.blog_title = 'Blog title is required';
    } else if (formData.blog_title.trim().length < 5) {
      newErrors.blog_title = 'Title must be at least 5 characters';
    }

    if (!formData.blog_by.trim()) {
      newErrors.blog_by = 'Author name is required';
    }

    if (!formData.blog.trim()) {
      newErrors.blog = 'Blog content is required';
    } else if (formData.blog.trim().length < 20) {
      newErrors.blog = 'Content must be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      const response = isEditMode
        ? await apiService.updateBlog(id, formData)
        : await apiService.createBlog(formData);

      if (response.data.success) {
        setSuccessMessage(isEditMode ? 'Blog updated successfully!' : 'Blog created successfully!');
        setTimeout(() => {
          navigate('/admin/blogs');
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to save blog');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save blog');
      console.error('Blog save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/blogs');
  };

  if (fetchLoading) {
    return (
      <div className="blog-form-container">
        <div className="loading">Loading blog...</div>
      </div>
    );
  }

  return (
    <div className="blog-form-container">
      <div className="blog-form-header">
        <h2>{isEditMode ? 'Edit Blog' : 'Create New Blog'}</h2>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="blog-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="blog_title">Blog Title *</label>
            <input
              type="text"
              id="blog_title"
              name="blog_title"
              value={formData.blog_title}
              onChange={handleChange}
              className={errors.blog_title ? 'error' : ''}
              disabled={loading}
              placeholder="Enter blog title"
            />
            {errors.blog_title && (
              <span className="error-message">{errors.blog_title}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="blog_by">Author Name *</label>
            <input
              type="text"
              id="blog_by"
              name="blog_by"
              value={formData.blog_by}
              onChange={handleChange}
              className={errors.blog_by ? 'error' : ''}
              disabled={loading}
              placeholder="Enter author name"
            />
            {errors.blog_by && (
              <span className="error-message">{errors.blog_by}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="blog">Blog Content *</label>
          <textarea
            id="blog"
            name="blog"
            value={formData.blog}
            onChange={handleChange}
            className={errors.blog ? 'error' : ''}
            disabled={loading}
            placeholder="Enter blog content"
            rows="12"
          />
          {errors.blog && (
            <span className="error-message">{errors.blog}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="image">Featured Image URL</label>
            <input
              type="text"
              id="image"
              name="image"
              value={formData.image}
              onChange={handleChange}
              disabled={loading}
              placeholder="Enter image URL (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="display_order">Display Order</label>
            <input
              type="number"
              id="display_order"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              disabled={loading}
              min="0"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="display_status">Status</label>
          <select
            id="display_status"
            name="display_status"
            value={formData.display_status}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update Blog' : 'Create Blog')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm;
